import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import useSafeToast from '../utils/safeToast';
import DeleteAccountModal from '../components/DeleteAccountModal';
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaCalendarAlt,
  FaHeart,
  FaBell,
  FaHome,
  FaMapMarkerAlt,
  FaStar,
  FaShieldAlt,
  FaCog,
  FaChevronRight,
  FaEdit,
  FaCheck,
  FaTimes,
  FaGlobe,
  FaRupeeSign,
  FaReceipt,
  FaCrown,
  FaCreditCard,
  FaLock,
  FaFileAlt,
  FaTrash,
  FaFolder,
  FaUniversity,
  FaReply,
  FaCamera
} from 'react-icons/fa';
import Navbar from '../components/Navbar';
import AccountCard from '../components/AccountCard';
import AccountNav from '../components/AccountNav';
import { getApiUrl } from '../config/api';
import { getPropertyImage, getImageUrl, getProfileImageUrl } from '../utils/imageHelper';
import { applyStoredLanguage, applyTranslationAndReload } from '../utils/languagePreference';
import { loadWishlistFavorites } from '../utils/wishlistStorage';
import NotificationSettingsSection from '../components/NotificationSettingsSection';
import './AccountPage.css';

const getFavoritesKey = () => {
  try {
    const user = localStorage.getItem('user');
    if (user) {
      const userObj = JSON.parse(user);
      return `favorites_${userObj._id || userObj.id}`;
    }
  } catch (e) {}
  return 'favorites';
};

const NAV_ITEMS = [
  { id: 'overview', label: 'Profile Overview', icon: FaUser },
  { id: 'personal', label: 'Personal Info', icon: FaFolder },
  { id: 'login', label: 'Login & Security', icon: FaLock },
  { id: 'preferences', label: 'Preferences', icon: FaGlobe },
  { id: 'notifications', label: 'Notifications', icon: FaBell },
  { id: 'payments', label: 'Payments & Payouts', icon: FaCreditCard },
  { id: 'bookings', label: 'Bookings', icon: FaReceipt },
  { id: 'wishlist', label: 'Wishlist', icon: FaHeart },
  { id: 'reviews', label: 'Reviews', icon: FaStar },
  { id: 'privacy', label: 'Privacy & Data', icon: FaFileAlt }
];

const ReviewsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }
    fetch(getApiUrl('/api/reviews/mine'), { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => res.json())
      .then((data) => {
        if (data.success && Array.isArray(data.reviews)) setReviews(data.reviews);
        else setReviews([]);
      })
      .catch(() => setReviews([]))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="account-reviews-loading">Loading your reviews...</div>;
  if (!reviews.length) {
    return (
      <div className="account-reviews-empty">
        <FaStar className="account-empty-icon" />
        <p>No reviews yet</p>
        <p className="account-empty-hint">Reviews you write for stays will appear here.</p>
        <Link to="/trips" className="account-reviews-write-link">Go to My Trips to write a review</Link>
      </div>
    );
  }
  return (
    <div className="account-reviews-section">
      <p className="account-reviews-subtitle">Reviews you wrote for stays</p>
      <div className="account-reviews-list">
        {reviews.map((r) => (
          <div key={r._id} className="account-review-card">
            <div className="account-review-header">
              <Link to={`/property/${r.propertyId}`} className="account-review-property">{r.propertyName}</Link>
              {r.propertyCity && <span className="account-review-location">{r.propertyCity}</span>}
            </div>
            <div className="account-review-rating-row">
              <span className="account-review-rating-label">Your rating:</span>
              <div className="account-review-stars">
                {[1, 2, 3, 4, 5].map((star) => (
                  <FaStar key={star} className={star <= (r.rating || 0) ? 'star-filled' : 'star-empty'} />
                ))}
              </div>
              <span className="account-review-rating-value">{r.rating}/5</span>
              <span className="account-review-date">{r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}</span>
            </div>
            {r.comment && <p className="account-review-comment">{r.comment}</p>}
            {r.hostReply && (
              <div className="account-review-reply">
                <strong>Host reply{r.hostRepliedAt ? ` (${new Date(r.hostRepliedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })})` : ''}:</strong>
                <p className="account-review-reply-text">{r.hostReply}</p>
              </div>
            )}
          </div>
        ))}
      </div>
      <Link to="/trips" className="account-reviews-write-link secondary">View My Trips</Link>
    </div>
  );
};

const PaymentHistorySection = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(getApiUrl('/api/payments/my-payments'), { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.payments) setPayments(data.payments);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="account-payment-history-loading">Loading payment history...</div>;
  if (!payments.length) return <div className="account-payment-history-empty">No online payments yet. Payments made at the hotel are not listed here.</div>;
  return (
    <div className="account-payment-history">
      <table className="account-payment-history-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Booking ID</th>
            <th>Property</th>
            <th>Amount</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {payments.map((p) => (
            <tr key={p._id}>
              <td>{p.paidAt ? new Date(p.paidAt).toLocaleDateString() : new Date(p.createdAt).toLocaleDateString()}</td>
              <td className="account-payment-booking-id">{p.bookingId ? String(p.bookingId).slice(-8) : '—'}</td>
              <td>{p.propertyName || '—'}</td>
              <td><FaRupeeSign />{Number(p.totalAmount).toLocaleString()}</td>
              <td><span className={`account-payment-status status-${p.status}`}>{p.status}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const VALID_SECTION_IDS = NAV_ITEMS.map((item) => item.id);

const AccountPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useSafeToast();
  const accountMainRef = React.useRef(null);
  const [user, setUser] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', phone: '', location: '' });
  const [saving, setSaving] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [uploadingProfilePic, setUploadingProfilePic] = useState(false);
  const profilePicInputRef = React.useRef(null);
  const [preferences, setPreferences] = useState(() => {
    try {
      const stored = localStorage.getItem('accountPreferences');
      return stored ? JSON.parse(stored) : { language: 'English', currency: 'INR' };
    } catch (e) {
      return { language: 'English', currency: 'INR' };
    }
  });

  /* Sync active section from URL hash on load and when hash changes (e.g. back button) */
  useEffect(() => {
    const hash = (location.hash || '').replace(/^#/, '').toLowerCase();
    if (hash && VALID_SECTION_IDS.includes(hash)) {
      setActiveSection(hash);
    }
  }, [location.hash]);

  const handleSectionSelect = (id) => {
    setActiveSection(id);
    navigate(`/account#${id}`, { replace: true });
    setTimeout(() => {
      accountMainRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (!token || !storedUser) {
      localStorage.setItem('authRedirectPath', '/account');
      navigate('/login');
      return;
    }

    try {
      const parsedUser = JSON.parse(storedUser);
      setUser(parsedUser);
      setEditForm({
        name: parsedUser.name || '',
        phone: parsedUser.phone || parsedUser.phoneNumber || '',
        location: parsedUser.location || ''
      });
      loadBookings(parsedUser);
      loadWishlistCount();
    } catch (e) {
      navigate('/login');
      return;
    }

    setLoading(false);
  }, [navigate]);

  const loadBookings = async (currentUser) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(getApiUrl('/api/bookings/stay/my-bookings'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const result = await response.json();

      if (result.success && result.bookings) {
        const u = currentUser || user || JSON.parse(localStorage.getItem('user') || '{}');
        const transformed = result.bookings.map((b) => ({
          bookingId: b._id,
          _id: b._id,
          status: b.status,
          property: b.property,
          checkIn: b.checkIn,
          checkOut: b.checkOut,
          guests: b.guests?.adults || 1,
          totalPrice: b.totalPrice,
          guestInfo: {
            firstName: u?.name?.split(' ')[0] || 'Guest',
            lastName: u?.name?.split(' ')[1] || '',
            email: u?.email || '',
            phone: u?.phone || ''
          },
          createdAt: b.createdAt
        }));
        setBookings(transformed);
      }
    } catch (e) {
      console.error('Error loading bookings:', e);
    }
  };

  const loadWishlistCount = () => {
    try {
      const favoritesKey = getFavoritesKey();
      const favorites = loadWishlistFavorites(favoritesKey);
      setWishlistCount(favorites.length);
    } catch (e) {}
  };

  const getUserInitials = () => {
    if (!user) return 'U';
    const base = user.name || user.email || '';
    const name = String(base).trim();
    if (!name) return 'U';
    const parts = name.split(' ');
    return parts.length === 1
      ? parts[0].charAt(0).toUpperCase()
      : (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  };

  const getMemberSince = () => {
    if (!user?.createdAt) return '2024';
    return new Date(user.createdAt).getFullYear().toString();
  };

  const notifyProfileUpdate = () => {
    window.dispatchEvent(new CustomEvent('userProfileUpdated'));
  };

  const getProfilePictureUrl = () => getProfileImageUrl(user?.profilePicture);

  const handleProfilePictureChange = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file (jpg, png, webp)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    setUploadingProfilePic(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('profilePicture', file);
      const response = await fetch(getApiUrl('/api/auth/upload-profile-picture'), {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const result = await response.json();
        if (result.success && result.user) {
          const updatedUser = { ...user, ...result.user, profilePicture: result.user.profilePicture };
          localStorage.setItem('user', JSON.stringify(updatedUser));
          setUser(updatedUser);
          notifyProfileUpdate();
          toast.success('Profile picture updated');
        } else {
        toast.error(result.message || 'Failed to update profile picture');
      }
    } catch (err) {
      console.error('Profile picture upload error:', err);
      toast.error('Failed to update profile picture');
    } finally {
      setUploadingProfilePic(false);
      if (profilePicInputRef.current) profilePicInputRef.current.value = '';
    }
  };

  const handleRemoveProfilePicture = async () => {
    setUploadingProfilePic(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/api/auth/remove-profile-picture'), {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.success && result.user) {
        const updatedUser = { ...user, ...result.user, profilePicture: null };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);
        notifyProfileUpdate();
        toast.success('Profile picture removed');
      } else {
        toast.error(result.message || 'Failed to remove profile picture');
      }
    } catch (err) {
      console.error('Remove profile picture error:', err);
      toast.error('Failed to remove profile picture');
    } finally {
      setUploadingProfilePic(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await fetch(getApiUrl('/api/auth/update-profile'), {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ name: editForm.name, phone: editForm.phone })
        });
        const result = await response.json();
        if (result.success && result.user) {
          const updatedUser = {
            ...user,
            ...result.user,
            phone: result.user.phone || result.user.phoneNumber,
            location: editForm.location
          };
          localStorage.setItem('user', JSON.stringify(updatedUser));
          setUser(updatedUser);
          setIsEditing(false);
          notifyProfileUpdate();
          toast.success('Profile updated successfully');
        } else {
          const updatedUser = { ...user, ...editForm };
          localStorage.setItem('user', JSON.stringify(updatedUser));
          setUser(updatedUser);
          setIsEditing(false);
          notifyProfileUpdate();
          toast.success('Profile updated');
        }
      } else {
        const updatedUser = { ...user, ...editForm };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);
        setIsEditing(false);
        notifyProfileUpdate();
        toast.success('Profile updated');
      }
    } catch (e) {
      console.error('Error saving profile:', e);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.current || !passwordForm.new || !passwordForm.confirm) {
      toast.error('Please fill all password fields');
      return;
    }
    if (passwordForm.new !== passwordForm.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.new.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    setChangingPassword(true);
    try {
      const response = await fetch(getApiUrl('/api/auth/change-password'), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          currentPassword: passwordForm.current,
          newPassword: passwordForm.new
        })
      });
      const result = await response.json();
      if (result.success) {
        toast.success('Password updated successfully');
        setShowPasswordModal(false);
        setPasswordForm({ current: '', new: '', confirm: '' });
      } else {
        toast.error(result.message || 'Failed to update password');
      }
    } catch (e) {
      toast.error('Failed to update password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeletingAccount(true);
    try {
      const { deleteUserAccount } = await import('../utils/deleteAccount');
      await deleteUserAccount();
      toast.success('Account deleted');
      setShowDeleteModal(false);
      navigate('/');
      window.location.reload();
    } catch (e) {
      toast.error(e.message || 'Failed to delete account');
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleDownloadData = () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const favoritesKey = getFavoritesKey();
      const favorites = loadWishlistFavorites(favoritesKey);
      const data = {
        exportedAt: new Date().toISOString(),
        user: userData,
        favorites,
        preferences
      };
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ubsaapp-data-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Data downloaded');
    } catch (e) {
      toast.error('Failed to download data');
    }
  };

  const handlePreferencesChange = (key, value) => {
    const updated = { ...preferences, [key]: value };
    setPreferences(updated);
    localStorage.setItem('accountPreferences', JSON.stringify(updated));
    if (key === 'language') applyStoredLanguage();
    if (key === 'currency') {
      const labels = { INR: 'Indian Rupee (₹)', USD: 'US Dollar ($)', EUR: 'Euro (€)', GBP: 'British Pound (£)' };
      toast.success(`Currency set to ${labels[value] || value}`);
    } else {
      toast.success('Preferences updated');
    }
  };

  const handleApplyLanguage = () => {
    toast.info('Applying language...', { autoClose: 800 });
    applyTranslationAndReload();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getTierLevel = () => {
    const tripCount = bookings.length;
    if (tripCount >= 10) return { name: 'Platinum', icon: FaCrown, color: 'var(--primary-color)' };
    if (tripCount >= 5) return { name: 'Gold', icon: FaStar, color: 'var(--accent-color)' };
    if (tripCount >= 2) return { name: 'Silver', icon: FaStar, color: 'var(--text-light)' };
    return { name: 'Explorer', icon: FaMapMarkerAlt, color: 'var(--secondary-color)' };
  };

  if (loading) {
    return (
      <div className="account-page page-content">
        <Navbar isScrolled={false} showLoginModal={true} />
        <div className="account-loading">
          <div className="account-spinner" />
          <p>Loading your account...</p>
        </div>
      </div>
    );
  }

  const tier = getTierLevel();
  const TierIcon = tier.icon;

  return (
    <div className="account-page page-content">
      <Navbar isScrolled={false} showLoginModal={true} />

      {/* Hero - Blurred background + floating profile card */}
      <div className="account-hero">
        <div className="account-hero-bg" />
        <div className="account-hero-inner">
          <motion.div
            className="account-profile-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="account-profile-card-inner">
              <input
                ref={profilePicInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleProfilePictureChange}
                className="account-avatar-input"
                aria-label="Upload profile picture"
              />
              <div
                className="account-avatar-wrapper account-avatar-upload"
                onClick={() => !uploadingProfilePic && profilePicInputRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && !uploadingProfilePic && profilePicInputRef.current?.click()}
                aria-label="Change profile picture"
              >
                {getProfilePictureUrl() ? (
                  <img src={getProfilePictureUrl()} alt={user.name} className="account-avatar-img" />
                ) : (
                  <div className="account-avatar">{getUserInitials()}</div>
                )}
                <span className="account-avatar-overlay">
                  <FaCamera /> {uploadingProfilePic ? 'Uploading...' : 'Change photo'}
                </span>
              </div>
              <div className="account-profile-info">
                <h1 className="account-hero-name">{user?.name || 'Traveler'}</h1>
                <div className="account-hero-email-row">
                  <span className="account-hero-email">{user?.email}</span>
                </div>
                <div className="account-hero-meta">
                  <span className="account-member-since">
                    <FaCalendarAlt /> Member since {getMemberSince()}
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="account-edit-profile-btn"
                onClick={() => { handleSectionSelect('personal'); setIsEditing(true); }}
              >
                Edit Profile
              </button>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="account-container">
        {/* Sidebar (desktop) / Full nav list (mobile) */}
        <aside className="account-sidebar">
          <span className="account-nav-mobile-label" aria-hidden="true">Account menu</span>
          <AccountNav items={NAV_ITEMS} activeId={activeSection} onSelect={handleSectionSelect} />
        </aside>

        {/* Main Content */}
        <main className="account-main" ref={accountMainRef}>
          <AnimatePresence mode="wait">
            {activeSection === 'overview' && (
              <motion.div
                key="overview"
                className="account-section"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.2 }}
              >
                <AccountCard
                  title="UBSAAPP Member"
                  subtitle="Earn rewards with every stay."
                >
                  <div className="account-loyalty-content">
                    <div className="account-loyalty-icon" style={{ color: tier.color }}>
                      <TierIcon />
                    </div>
                    <div className="account-loyalty-progress">
                      <div
                        className="account-loyalty-bar"
                        style={{
                          width: `${Math.min((bookings.length / 10) * 100, 100)}%`,
                          backgroundColor: tier.color
                        }}
                      />
                    </div>
                    <p className="account-loyalty-hint">
                      {bookings.length < 2
                        ? '2 more trips to reach Silver'
                        : bookings.length < 5
                          ? `${5 - bookings.length} more trips to reach Gold`
                          : bookings.length < 10
                            ? `${10 - bookings.length} more trips to reach Platinum`
                            : "You've reached Platinum! 🎉"}
                    </p>
                  </div>
                </AccountCard>

                <div className="account-stats-grid">
                  <Link to="/trips" className="account-stat-card">
                    <div className="account-stat-icon trips">
                      <FaReceipt />
                    </div>
                    <div className="account-stat-content">
                      <span className="account-stat-value">{bookings.length}</span>
                      <span className="account-stat-label">Trips</span>
                    </div>
                  </Link>
                  <Link to="/wishlist" className="account-stat-card">
                    <div className="account-stat-icon wishlist">
                      <FaHeart />
                    </div>
                    <div className="account-stat-content">
                      <span className="account-stat-value">{wishlistCount}</span>
                      <span className="account-stat-label">Saved stays</span>
                    </div>
                  </Link>
                  <Link to="/notifications" className="account-stat-card">
                    <div className="account-stat-icon notifications">
                      <FaBell />
                    </div>
                    <div className="account-stat-content">
                      <span className="account-stat-value">—</span>
                      <span className="account-stat-label">Notifications</span>
                    </div>
                  </Link>
                </div>

                <AccountCard title="Quick actions">
                  <div className="account-actions-grid">
                    <Link to="/trips" className="account-action-btn">
                      <FaReceipt />
                      <span>My bookings</span>
                      <FaChevronRight />
                    </Link>
                    <Link to="/wishlist" className="account-action-btn">
                      <FaHeart />
                      <span>Wishlist</span>
                      <FaChevronRight />
                    </Link>
                    <Link to="/notifications" className="account-action-btn">
                      <FaBell />
                      <span>Notifications</span>
                      <FaChevronRight />
                    </Link>
                    <Link to="/my-payments" className="account-action-btn">
                      <FaCreditCard />
                      <span>Payment History</span>
                      <FaChevronRight />
                    </Link>
                    <Link to="/host" className="account-action-btn host">
                      <FaHome />
                      <span>Become a host</span>
                      <FaChevronRight />
                    </Link>
                  </div>
                </AccountCard>

                {bookings.length > 0 && (
                  <AccountCard
                    title="Upcoming Bookings"
                    headerAction={
                      <Link to="/trips" className="account-card-link">
                        View Details
                      </Link>
                    }
                  >
                    <div className="account-recent-list">
                      {bookings.slice(0, 3).map((booking) => (
                        <Link
                          key={booking._id}
                          to={`/booking-details/${booking.bookingId || booking._id}`}
                          state={{ booking }}
                          className="account-recent-item"
                        >
                          <div className="account-recent-image">
                            <img
                              src={getPropertyImage(
                                booking.property,
                                'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=200'
                              )}
                              alt={booking.property?.name}
                            />
                          </div>
                          <div className="account-recent-info">
                            <h4>{booking.property?.name}</h4>
                            <p>
                              <FaMapMarkerAlt /> {booking.property?.city}
                            </p>
                            <span className="account-recent-date">
                              {formatDate(booking.checkIn)} – {formatDate(booking.checkOut)}
                            </span>
                          </div>
                          <span className={`account-recent-status ${booking.status}`}>
                            {booking.status}
                          </span>
                        </Link>
                      ))}
                    </div>
                  </AccountCard>
                )}
              </motion.div>
            )}

            {activeSection === 'personal' && (
              <motion.div
                key="personal"
                className="account-section"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.2 }}
              >
                <AccountCard
                  title="Personal info"
                  subtitle="Update your name, phone, and location"
                  headerAction={
                    !isEditing ? (
                      <button
                        type="button"
                        className="account-edit-btn"
                        onClick={() => setIsEditing(true)}
                      >
                        <FaEdit /> Edit
                      </button>
                    ) : (
                      <div className="account-edit-actions">
                        <button
                          type="button"
                          className="account-save-btn"
                          onClick={handleSaveProfile}
                          disabled={saving}
                        >
                          <FaCheck /> {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          type="button"
                          className="account-cancel-btn"
                          onClick={() => {
                            setIsEditing(false);
                            setEditForm({
                              name: user?.name || '',
                              phone: user?.phone || '',
                              location: user?.location || ''
                            });
                          }}
                        >
                          <FaTimes /> Cancel
                        </button>
                      </div>
                    )
                  }
                >
                  <div className="account-profile-form">
                    <div className="account-form-group account-form-group-photo">
                      <label>
                        <FaUser /> Profile picture
                      </label>
                      <div className="account-photo-row">
                        <div className="account-photo-preview">
                          {getProfilePictureUrl() ? (
                            <img src={getProfilePictureUrl()} alt={user?.name} />
                          ) : (
                            <div className="account-photo-placeholder">{getUserInitials()}</div>
                          )}
                        </div>
                        <div className="account-photo-actions">
                          <button
                            type="button"
                            className="account-photo-btn account-photo-btn-change"
                            onClick={() => !uploadingProfilePic && profilePicInputRef.current?.click()}
                            disabled={uploadingProfilePic}
                          >
                            <FaCamera /> Change photo
                          </button>
                          {user?.profilePicture && (
                            <button
                              type="button"
                              className="account-photo-btn account-photo-btn-remove"
                              onClick={handleRemoveProfilePicture}
                              disabled={uploadingProfilePic}
                            >
                              <FaTrash /> Remove
                            </button>
                          )}
                        </div>
                      </div>
                      {uploadingProfilePic && <p className="account-photo-hint">Uploading...</p>}
                    </div>
                    <div className="account-form-group">
                      <label>
                        <FaUser /> Full name
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.name}
                          onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                          placeholder="Your name"
                        />
                      ) : (
                        <div className="account-form-value">{user?.name || 'Not set'}</div>
                      )}
                    </div>
                    <div className="account-form-group">
                      <label>
                        <FaEnvelope /> Email
                      </label>
                      <div className="account-form-value account-form-readonly">{user?.email}</div>
                    </div>
                    <div className="account-form-group">
                      <label>
                        <FaPhone /> Phone
                      </label>
                      {isEditing ? (
                        <input
                          type="tel"
                          value={editForm.phone}
                          onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                          placeholder="+91 98765 43210"
                        />
                      ) : (
                        <div className="account-form-value">{user?.phone || 'Not set'}</div>
                      )}
                    </div>
                    <div className="account-form-group">
                      <label>
                        <FaMapMarkerAlt /> Location
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.location}
                          onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                          placeholder="City, Country"
                        />
                      ) : (
                        <div className="account-form-value">{user?.location || 'Not set'}</div>
                      )}
                    </div>
                  </div>
                  <div className="account-security-badge">
                    <FaShieldAlt />
                    <span>Your data is secured</span>
                  </div>
                </AccountCard>
              </motion.div>
            )}

            {activeSection === 'login' && (
              <motion.div
                key="login"
                className="account-section"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.2 }}
              >
                <AccountCard
                  title="Login & security"
                  subtitle="Manage your password and login methods"
                >
                  <div className="account-settings-list">
                    <div className="account-setting-item">
                      <div className="account-setting-icon">
                        <FaEnvelope />
                      </div>
                      <div>
                        <strong>Email</strong>
                        <p>{user?.email}</p>
                      </div>
                      <span className="account-setting-badge">Primary</span>
                    </div>
                    <div className="account-setting-item">
                      <div className="account-setting-icon">
                        <FaLock />
                      </div>
                      <div>
                        <strong>Password</strong>
                        <p>••••••••••</p>
                      </div>
                      <button
                        type="button"
                        className="account-link-btn"
                        onClick={() => setShowPasswordModal(true)}
                      >
                        Update
                      </button>
                    </div>
                  </div>
                </AccountCard>
              </motion.div>
            )}

            {activeSection === 'preferences' && (
              <motion.div
                key="preferences"
                className="account-section"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.2 }}
              >
                <AccountCard
                  title="Preferences"
                  subtitle="Language, currency, and regional settings"
                >
                  <div className="account-preferences">
                    <div className="account-pref-item account-pref-item-language">
                      <FaGlobe className="account-pref-icon" />
                      <div className="account-pref-field">
                        <strong>Language</strong>
                        <select
                          value={preferences.language}
                          onChange={(e) => handlePreferencesChange('language', e.target.value)}
                          className="account-pref-select"
                          aria-label="Select language"
                        >
                          <option value="English">English</option>
                          <option value="Hindi">Hindi</option>
                          <option value="Kannada">Kannada</option>
                          <option value="Tamil">Tamil</option>
                          <option value="Telugu">Telugu</option>
                          <option value="Bengali">Bengali</option>
                          <option value="Marathi">Marathi</option>
                          <option value="Malayalam">Malayalam</option>
                          <option value="Gujarati">Gujarati</option>
                          <option value="Punjabi">Punjabi</option>
                          <option value="Urdu">Urdu</option>
                          <option value="Odia">Odia</option>
                          <option value="Sanskrit">Sanskrit</option>
                          <option value="Spanish">Spanish</option>
                          <option value="French">French</option>
                          <option value="German">German</option>
                          <option value="Arabic">Arabic</option>
                        </select>
                        <button
                          type="button"
                          className="account-apply-language-btn"
                          onClick={handleApplyLanguage}
                        >
                          Apply
                        </button>
                      </div>
                      <FaChevronRight className="account-pref-chevron" />
                    </div>
                    <div className="account-pref-item">
                      <FaRupeeSign className="account-pref-icon account-pref-icon-currency" />
                      <div className="account-pref-field">
                        <strong>Currency</strong>
                        <select
                          value={preferences.currency}
                          onChange={(e) => handlePreferencesChange('currency', e.target.value)}
                          className="account-pref-select"
                          aria-label="Select currency"
                        >
                          <option value="INR">Indian Rupee (₹)</option>
                          <option value="USD">US Dollar ($)</option>
                          <option value="EUR">Euro (€)</option>
                          <option value="GBP">British Pound (£)</option>
                        </select>
                      </div>
                      <FaChevronRight className="account-pref-chevron" />
                    </div>
                  </div>
                </AccountCard>
              </motion.div>
            )}

            {activeSection === 'notifications' && (
              <motion.div
                key="notifications"
                className="account-section"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.2 }}
              >
                <AccountCard
                  title="Notifications"
                  subtitle="Manage browser push alerts and notification categories"
                >
                  <NotificationSettingsSection />
                </AccountCard>
              </motion.div>
            )}

            {activeSection === 'payments' && (
              <motion.div
                key="payments"
                className="account-section"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.2 }}
              >
                <AccountCard
                  title="Payments & Payouts"
                  subtitle="Your online payment history"
                >
                  <PaymentHistorySection />
                </AccountCard>
              </motion.div>
            )}

            {activeSection === 'bookings' && (
              <motion.div
                key="bookings"
                className="account-section"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.2 }}
              >
                <AccountCard
                  title="Bookings"
                  subtitle="View and manage all your upcoming and past trips"
                >
                  <Link to="/trips" className="account-primary-btn">
                    View all bookings
                    <FaChevronRight />
                  </Link>
                </AccountCard>
              </motion.div>
            )}

            {activeSection === 'wishlist' && (
              <motion.div
                key="wishlist"
                className="account-section"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.2 }}
              >
                <AccountCard
                  title="Wishlist"
                  subtitle={`${wishlistCount} saved stays`}
                >
                  <Link to="/wishlist" className="account-primary-btn">
                    View wishlist
                    <FaChevronRight />
                  </Link>
                </AccountCard>
              </motion.div>
            )}

            {activeSection === 'reviews' && (
              <motion.div
                key="reviews"
                className="account-section"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.2 }}
              >
                <AccountCard
                  title="Reviews"
                  subtitle="Reviews you've written for stays"
                >
                  <ReviewsSection />
                </AccountCard>
              </motion.div>
            )}

            {activeSection === 'privacy' && (
              <motion.div
                key="privacy"
                className="account-section"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.2 }}
              >
                <AccountCard
                  title="Privacy & data"
                  subtitle="Manage your data and privacy settings"
                >
                  <div className="account-settings-list">
                    <div className="account-setting-item">
                      <div className="account-setting-icon">
                        <FaFileAlt />
                      </div>
                      <div>
                        <strong>Download your data</strong>
                        <p>Get a copy of your UBSAAPP data</p>
                      </div>
                      <button
                        type="button"
                        className="account-link-btn"
                        onClick={handleDownloadData}
                      >
                        Download
                      </button>
                    </div>
                    <div className="account-setting-item">
                      <div className="account-setting-icon danger">
                        <FaTrash />
                      </div>
                      <div>
                        <strong>Delete account</strong>
                        <p>Permanently delete your account and data</p>
                      </div>
                      <button
                        type="button"
                        className="account-link-btn danger"
                        onClick={() => setShowDeleteModal(true)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </AccountCard>
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      </div>

      {/* Password Update Modal */}
      {showPasswordModal && (
        <div className="account-modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="account-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Change password</h3>
            <p className="account-modal-desc">Enter your current password and choose a new one.</p>
            <div className="account-form-group">
              <label>Current password</label>
              <input
                type="password"
                value={passwordForm.current}
                onChange={(e) => setPasswordForm((p) => ({ ...p, current: e.target.value }))}
                placeholder="Current password"
              />
            </div>
            <div className="account-form-group">
              <label>New password</label>
              <input
                type="password"
                value={passwordForm.new}
                onChange={(e) => setPasswordForm((p) => ({ ...p, new: e.target.value }))}
                placeholder="At least 6 characters"
              />
            </div>
            <div className="account-form-group">
              <label>Confirm new password</label>
              <input
                type="password"
                value={passwordForm.confirm}
                onChange={(e) => setPasswordForm((p) => ({ ...p, confirm: e.target.value }))}
                placeholder="Confirm new password"
              />
            </div>
            <div className="account-modal-actions">
              <button
                type="button"
                className="account-cancel-btn"
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordForm({ current: '', new: '', confirm: '' });
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="account-save-btn"
                onClick={handleChangePassword}
                disabled={changingPassword}
              >
                {changingPassword ? 'Updating...' : 'Update password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="account-modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="account-modal account-modal-danger" onClick={(e) => e.stopPropagation()}>
            <h3>Delete account</h3>
            <p className="account-modal-desc">
              This will permanently delete your account and all associated data. This action cannot be undone.
            </p>
            <div className="account-modal-actions">
              <button
                type="button"
                className="account-cancel-btn"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="account-delete-btn"
                onClick={handleDeleteAccount}
                disabled={deletingAccount}
              >
                {deletingAccount ? 'Deleting...' : 'Delete account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountPage;
